import { useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import {
  buildDoughnutChartOptions,
  chartHasPositiveSegments,
  mergeDoughnutChartData,
  splitTooltipPercentAndName,
  type ConnectionChartData,
  type ConnectionChartTooltipState,
} from './ConnectionChart.helpers';
import { formatConnectionUsd } from '../CounterpartyConnections/CounterpartyConnections.helpers';
import styles from './ConnectionChart.module.sass';

ChartJS.register(ArcElement, ChartTooltip);

const Tooltip = ({
  opacity,
  label,
  left,
  top,
  color,
}: ConnectionChartTooltipState) => {
  const { percent, name } = splitTooltipPercentAndName(label ?? '');
  return (
    <div
      className={styles.tooltip}
      style={{
        opacity,
        left: `${left}px`,
        top: `${top}px`,
      }}
    >
      <div className={styles.tooltip__content}>
        <div className={styles.tooltip__value}>
          {percent ? (
            <span
              className={styles.tooltip__accent}
              style={color ? { color } : undefined}
            >
              {percent}
            </span>
          ) : null}
          {name ? (
            <>
              {percent ? ' ' : null}
              <span className={styles.tooltip__name}>{name}</span>
            </>
          ) : null}
          {!percent && !name && label ? (
            <span className={styles.tooltip__name}>{label}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface ConnectionChartProps {
  chartData: ConnectionChartData;
  centerTitle: string;
  centerAmount: number | null;
  flowDirection: 'received' | 'sent';
}

const ConnectionChart = ({
  chartData,
  centerTitle,
  centerAmount,
  flowDirection,
}: ConnectionChartProps) => {
  const [tooltipData, setTooltipData] =
    useState<ConnectionChartTooltipState | null>(null);

  const mergedData = useMemo(
    () => mergeDoughnutChartData(chartData),
    [chartData],
  );

  const chartOptions = useMemo(
    () => buildDoughnutChartOptions(setTooltipData),
    [],
  );

  const hasSegments = useMemo(
    () => chartHasPositiveSegments(chartData),
    [chartData],
  );

  const titleArrow = flowDirection === 'sent' ? '\u2191' : '\u2193';

  const centerUsdLabel =
    centerAmount !== null
      ? formatConnectionUsd(centerAmount, undefined, {
          omitFractionIfWhole: true,
        })
      : null;

  return (
    <div className={styles.chartContainer}>
      {hasSegments ? (
        <>
          <Doughnut options={chartOptions} data={mergedData} />
          <div className={styles.centerText}>
            <div className={styles.centerText__title}>
              <span>{centerTitle}</span>
              <span className={styles.centerText__arrow} aria-hidden>
                {titleArrow}
              </span>
            </div>
            {centerUsdLabel !== null && (
              <div className={styles.centerText__value}>{centerUsdLabel}</div>
            )}
          </div>
          {tooltipData && <Tooltip {...tooltipData} />}
        </>
      ) : (
        <div className={styles.empty}>
          <div className={styles.centerText__title}>
            <span>{centerTitle}</span>
            <span className={styles.centerText__arrow} aria-hidden>
              {titleArrow}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionChart;
